/**
 * «Посты» во вкладке «Лиды ВК»: что выложили в сообщество и что это дало —
 * охват (люди; у клипа — лента клипов), доля вирального, действия, охват на
 * 2-й и 7-й день жизни поста; ниже — сообщество по дням. Копия статистики ВК
 * (`vk_post_stat`, фон `vk_stats_sync`). Спека
 * `docs/specs/2026-10-01-vk-posts-design.md`; числа — `prokat-runner vk-posts`.
 */
import { useQuery } from "@tanstack/react-query";

import { type VkPostRow, loadVkPosts } from "@/lib/vkPosts";

const num = (x: number | null | undefined) =>
  x === null || x === undefined ? "—" : x.toLocaleString("ru-RU");
const pct = (x: number | null) =>
  x === null ? "—" : `${Math.round(x * 100)}%`;
const when = (s: string) =>
  `${s.slice(8, 10)}.${s.slice(5, 7)} ${s.slice(11, 16)}`;

const COLS: { key: keyof VkPostRow; label: string; hint: string }[] = [
  { key: "reach", label: "Охват", hint: "уникальные люди; у клипа — лента клипов, не стена" },
  { key: "reach_subscribers", label: "Подписчики", hint: "охват подписчиков" },
  { key: "reach_viral", label: "Виральный", hint: "увидели не подписчики: рекомендации, репосты, поиск" },
  { key: "viral_share", label: "Доля", hint: "виральный / охват — вышел ли пост за своих" },
  { key: "views", label: "Просмотры", hint: "показы, один человек — несколько" },
  { key: "likes", label: "Лайки", hint: "" },
  { key: "reposts", label: "Поделились", hint: "" },
  { key: "to_group", label: "В группу", hint: "переходы в сообщество с поста" },
  { key: "links", label: "Ссылки", hint: "переходы по ссылкам в посте" },
  { key: "joins", label: "Подписки", hint: "подписались с поста" },
  { key: "reach_2d", label: "2-й день", hint: "охват на 2-й день жизни — сравнение постов на одном сроке" },
  { key: "reach_7d", label: "7-й день", hint: "охват на 7-й день жизни" },
];

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <div
      className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3"
      data-annot={`посты вк «${label}»`}
    >
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[22px] font-semibold leading-none tabular-nums">
        {value}
      </div>
      {hint && <div className="text-[11.5px] text-fg-faint">{hint}</div>}
    </div>
  );
}

export function VkPosts({ from, to }: { from: string; to: string }) {
  const q = useQuery({
    queryKey: ["vk-posts", from, to],
    queryFn: () => loadVkPosts(from, to),
  });
  if (q.isPending)
    return <div className="text-[12.5px] text-fg-muted">Считаю посты ВК…</div>;
  if (q.isError)
    return (
      <div className="text-[12.5px] text-err">
        Не удалось получить посты: {String(q.error.message)}
      </div>
    );
  const r = q.data;
  const t = r.total;
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  const td = "whitespace-nowrap px-3 py-1 tabular-nums text-right";
  const days = [...r.days].reverse();
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat
          label="Постов"
          value={t.posts}
          hint={r.kinds.map((k) => `${k.label} ${k.posts}`).join(" · ")}
        />
        <Stat
          label="Охват в среднем на пост"
          value={num(t.reach_avg)}
          hint={r.kinds.map((k) => `${k.label} ${num(k.reach_avg)}`).join(" · ")}
        />
        <Stat
          label="Виральный охват"
          value={pct(t.viral_share)}
          hint={r.kinds.map((k) => `${k.label} ${pct(k.viral_share)}`).join(" · ")}
        />
        <Stat
          label="Переходы в группу"
          value={t.to_group}
          hint={`по ссылкам ${t.links} · подписались ${t.joins}`}
        />
        <Stat
          label="Лайки"
          value={t.likes}
          hint={`поделились ${t.reposts} · комментарии ${t.comments}`}
        />
        <Stat
          label="Скрыли"
          value={t.hides}
          hint={r.stat_date ? `снимок ВК ${r.stat_date}` : "снимков ещё нет"}
        />
      </div>
      <section
        className="rounded-[var(--radius-card)] border border-border bg-bg-subtle"
        data-annot="посты вк таблица"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr>
                <th className={`${th} pl-4`}>Пост · {r.rows.length}</th>
                {COLS.map((c) => (
                  <th key={c.key} className={`${th} text-right`} title={c.hint}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {r.rows.map((p) => (
                <tr key={p.post_id} className="border-t border-border">
                  <td className="max-w-[360px] truncate py-1 pl-4 pr-3">
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:underline"
                      title={p.text}
                    >
                      <span className="tabular-nums text-fg-muted">
                        {when(p.date)}
                      </span>{" "}
                      <span className="text-fg-faint">{p.kind_label}</span>{" "}
                      {p.text || "без текста"}
                    </a>
                  </td>
                  {COLS.map((c) => (
                    <td key={c.key} className={td}>
                      {c.key === "viral_share"
                        ? pct(p.viral_share)
                        : num(p[c.key] as number | null)}
                    </td>
                  ))}
                </tr>
              ))}
              {r.rows.length === 0 && (
                <tr className="border-t border-border">
                  <td
                    colSpan={COLS.length + 1}
                    className="px-4 py-3 text-fg-muted"
                  >
                    За период постов нет.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      {days.length > 0 && (
        <section
          className="rounded-[var(--radius-card)] border border-border bg-bg-subtle"
          data-annot="посты вк сообщество по дням"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th className={`${th} pl-4`}>Сообщество по дням</th>
                  <th className={`${th} text-right`}>Постов</th>
                  <th className={`${th} text-right`} title="уникальные посетители сообщества">
                    Посетители
                  </th>
                  <th className={`${th} text-right`} title="просмотры страницы сообщества">
                    Заходы
                  </th>
                  <th className={`${th} text-right`}>Подписчиков</th>
                  <th className={`${th} text-right`} title="охват всех постов за день">
                    Охват постов
                  </th>
                  <th className={`${th} text-right`} title="охват всех клипов за день">
                    Охват клипов
                  </th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => (
                  <tr key={d.day} className="border-t border-border">
                    <td className="py-1 pl-4 pr-3 tabular-nums">
                      {d.day.slice(8, 10)}.{d.day.slice(5, 7)}
                    </td>
                    <td className={td}>{d.posts ?? ""}</td>
                    <td className={td}>{num(d.visitors)}</td>
                    <td className={td}>{num(d.visits)}</td>
                    <td className={td}>{num(d.subscribers)}</td>
                    <td className={td}>{num(d.posts_reach)}</td>
                    <td className={td}>{num(d.clips_reach)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
